// Roles & permissions API hooks (spec 2026-09-04-02).
//
// Kept beside multiOrg.ts — roles are the third leg of the members surface —
// but typed through the generated openapi-react-query client (`$api`) rather
// than hand-rolled casts, since these endpoints landed with a complete schema.

import { useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { $api } from './queryClient'
import type { components, Permission } from './schema'

export type RoleView = components['schemas']['RoleView']
export type PermissionEntry = components['schemas']['PermissionEntry']

/** Resources in the order the UI must present them (spec §"Permission catalogue"). */
export const RESOURCE_ORDER = [
    'assets',
    'documents',
    'interventions',
    'issues',
    'contracts',
    'finance',
    'capex',
    'compliance',
    'contacts',
    'configuration',
    'members',
    'organization',
    'data',
    'conversations',
    'group_steering',
    'data_protection',
    'signatures',
    'billing',
    'archive',
    'knowledge',
    'campaigns',
    'listings',
    'rental_applications',
    'webhooks',
    'rental_feed',
] as const

export type Resource = (typeof RESOURCE_ORDER)[number]

/**
 * The two base actions, the ones every resource offers. They are the two
 * columns the editor always draws (`authz.Actions`).
 */
export const ACTIONS = ['read', 'write'] as const
export type BaseAction = (typeof ACTIONS)[number]

/**
 * The narrowed variants (`authz` qualified actions). Listing them here types
 * the labels and orders the badges; WHICH resource offers WHICH of them is
 * never decided here — that comes from `GET …/permissions`, whose catalogue is
 * built from `authz.QualifiedActions`. A hard-coded resource→actions map in the
 * frontend is exactly the drift this spec is undoing.
 */
export const QUALIFIED_ACTIONS = [
    'read_shared',
    'read_own',
    'write_own',
    'read_assigned',
    'write_assigned',
    'portfolios',
] as const
export type QualifiedAction = (typeof QUALIFIED_ACTIONS)[number]

export type Action = BaseAction | QualifiedAction

/**
 * The three "narrowings" a qualified action expresses, which is what the
 * comparison matrix renders as a badge: `read_own` and `write_own` are the same
 * narrowing seen through the two base actions.
 */
export type ActionVariant = 'shared' | 'own' | 'assigned' | 'portfolios'

const VARIANT_OF: Record<QualifiedAction, ActionVariant> = {
    read_shared: 'shared',
    read_own: 'own',
    write_own: 'own',
    read_assigned: 'assigned',
    write_assigned: 'assigned',
    portfolios: 'portfolios',
}

/** The narrowing a qualified action expresses. */
export function actionVariant(action: QualifiedAction): ActionVariant {
    return VARIANT_OF[action]
}

/** True for a qualified action that narrows `write` rather than `read`. */
export function isWriteAction(action: Action): boolean {
    return action === 'write' || action.startsWith('write_')
}

export function isQualifiedAction(action: string): action is QualifiedAction {
    return (QUALIFIED_ACTIONS as readonly string[]).includes(action)
}

/**
 * Built-in role codes, immutable in every org, strongest first — the exact
 * order of `authz.BuiltinRoleCodes`, which is also the order `GET …/roles`
 * serves them in. The list is a fallback ordering for a client that has to
 * rank roles without the API's own ordering (and a place to spot drift); the
 * roles page itself renders in API order and never re-sorts.
 */
export const BUILTIN_ROLE_CODES = [
    'owner',
    'admin',
    'member',
    'technical_manager',
    'viewer',
    'guest',
    'resident',
    'property_owner',
    'provider',
] as const

export function permissionCode(resource: string, action: Action): Permission {
    return `${resource}.${action}` as Permission
}

/**
 * The permission catalogue. It is a fixed, server-owned list — hence the long
 * stale time: it cannot change without a deploy.
 */
export function usePermissionCatalogue(orgCode: string) {
    return $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/permissions',
        { params: { path: { orgCode } } },
        { enabled: !!orgCode, staleTime: 1000 * 60 * 60 }
    )
}

/**
 * The qualified actions each resource offers, keyed by resource, in catalogue
 * order — derived from the catalogue endpoint, never from a list written here.
 *
 * That is the whole point: `documents` offers `read_shared` only, `issues`
 * offers three, `contracts` offers none, and the day a resource becomes
 * scope-aware in `internal/authz` the UI grows its column without a frontend
 * change. Resources with no narrowed action are simply absent from the map, so
 * `map[resource]` being undefined is the "no Limited cell here" answer.
 */
export function useQualifiedActionsByResource(orgCode: string): {
    byResource: Record<string, QualifiedAction[]>
    isLoading: boolean
} {
    const { data, isLoading } = usePermissionCatalogue(orgCode)

    const byResource = useMemo(() => {
        const grouped: Record<string, QualifiedAction[]> = {}
        for (const entry of data?.data ?? []) {
            if (!isQualifiedAction(entry.action)) continue
            ;(grouped[entry.resource] ??= []).push(entry.action)
        }
        return grouped
    }, [data])

    return { byResource, isLoading }
}

/**
 * The org's roles — the nine built-ins plus any custom ones, in the order the
 * API decided (built-ins strongest first, then custom by code). Render them in
 * that order; the frontend is not a second ordering authority. Needs `members.read`;
 * a caller without it gets a 403 which the client-level handler turns into the
 * standard notification.
 */
export function useRoles(orgCode: string, enabled = true) {
    return $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/roles',
        { params: { path: { orgCode } } },
        { enabled: !!orgCode && enabled }
    )
}

/**
 * Mutations on custom roles. Built-ins are refused server-side with 409
 * `builtin_role_immutable`; the UI does not offer the controls at all, this is
 * only the second line of defence.
 */
export function useRoleMutations(orgCode: string) {
    const queryClient = useQueryClient()

    // Roles feed three caches: the roles list itself, the members list (role
    // labels) and the membership summary (the caller's own permission set,
    // which changes when their role's bundle changes).
    const invalidate = () => {
        queryClient.invalidateQueries({
            predicate: (query) => {
                const key = query.queryKey as unknown[]
                if (key[0] === 'my-organizations' || key[0] === 'members') return true
                return (
                    key.length >= 2 &&
                    key[0] === 'get' &&
                    key[1] === '/api/v1/organizations/{orgCode}/roles'
                )
            },
        })
    }

    const create = $api.useMutation(
        'post',
        '/api/v1/organizations/{orgCode}/roles',
        { onSuccess: invalidate }
    )
    const update = $api.useMutation(
        'patch',
        '/api/v1/organizations/{orgCode}/roles/{roleUid}',
        { onSuccess: invalidate }
    )
    const remove = $api.useMutation(
        'delete',
        '/api/v1/organizations/{orgCode}/roles/{roleUid}',
        { onSuccess: invalidate }
    )

    return { create, update, remove, orgCode }
}
